<template>
	<div>
		<!-- 使用自定义对话框组件 -->
		<non-modal-dialog :visible="showRoom" :title="title" :width="windowSize.width" :height="windowSize.height"
			:taskbar-status="taskbarStatusText" @close="onQuit" @fullscreen-change="onFullScreenChange">

			<!-- 任务栏头像插槽 -->
			<template #taskbar-avatar>
				<head-image :size="40" :name="friend.showNickName" :url="friend.headImage" radius="50%"></head-image>
			</template>

			<!-- 对话框内容 -->
			<div class="rtc-private-video" :class="{ 'is-fullscreen': isFullScreen }">
				<div v-if="deviceError" class="rtc-reconnect-banner rtc-device-error-banner">
					<span>{{ deviceError.message }}</span>
					<span class="rtc-retry-btn" @click="retryOpenLocalTracks">{{ $t('rtc.retry') }}</span>
				</div>
				<div v-else-if="isReconnecting" class="rtc-reconnect-banner">{{ $t('rtc.reconnecting') }}</div>
				<div v-else-if="isPoorNetwork" class="rtc-reconnect-banner">{{ $t('rtc.poorNetwork') }}</div>
				<div v-show="isVideo" class="rtc-video-box">
					<div class="rtc-video-friend" v-loading="!isChating" :element-loading-text="$t('rtc.waitingAnswer')"
						element-loading-background="rgba(0, 0, 0, 0.1)">
						<div v-show="showRemoteVideo" ref="remoteVideoContainer" class="rtc-video-el"
							:class="{ 'rtc-video-remote-share': isRemoteShareScreen }"></div>
						<div v-show="!showRemoteVideo" class="rtc-avatar-panel">
							<div class="rtc-avatar-bg" :style="avatarBlurBg(friend.headImage, '#3a3a3a')"></div>
							<div class="rtc-avatar-dim"></div>
							<head-image class="rtc-avatar-front" :size="200" :name="friend.showNickName"
								:url="friend.headImage" :isShowUserInfo="false"></head-image>
						</div>
						<div v-show="isChating && !isRemoteMicroPhone" class="rtc-remote-mic-badge"
							:title="$t('rtc.remoteMuted')">
							<span class="icon iconfont icon-microphone-off"></span>
						</div>
					</div>
					<div class="rtc-video-mine">
						<div v-show="isCamera || isShareScreen" ref="localVideoContainer" class="rtc-video-el"
							:class="{ 'rtc-video-local-share': isShareScreen }"></div>
						<div v-show="!isCamera && !isShareScreen" class="rtc-avatar-panel rtc-avatar-panel-mine">
							<div class="rtc-avatar-bg" :style="avatarBlurBg(userInfo.headImageThumb, '#555')"></div>
							<div class="rtc-avatar-dim rtc-avatar-dim-mine"></div>
							<head-image class="rtc-avatar-front" :size="80" :name="userInfo.nickName"
								:url="userInfo.headImageThumb" :isShowUserInfo="false"></head-image>
						</div>
					</div>
				</div>
				<div v-show="!isVideo" class="rtc-voice-box" v-loading="!isChating"
					:element-loading-text="$t('rtc.waitingAnswer')" element-loading-background="rgba(0, 0, 0, 0.1)">
					<head-image class="friend-head-image" :size="200" :name="friend.showNickName"
						:url="friend.headImage" :isShowUserInfo="false">
						<div class="rtc-voice-name">{{ friend.showNickName }}</div>
					</head-image>
					<div v-show="isChating && !isRemoteMicroPhone" class="rtc-remote-mic-badge rtc-remote-mic-badge-voice"
						:title="$t('rtc.remoteMuted')">
						<span class="icon iconfont icon-microphone-off"></span>
					</div>
				</div>
				<div class="rtc-control-bar">
					<div v-show="isMicroPhone" class="icon iconfont icon-microphone-on icon-front"
						@click="onSwitchMicroPhone" :title="$t('rtc.micOn')"></div>
					<div v-show="!isMicroPhone" class="icon iconfont icon-microphone-off icon-back"
						@click="onSwitchMicroPhone" :title="$t('rtc.micOff')"></div>
					<div v-show="isSpeaker" class="icon iconfont icon-speaker-on icon-front" @click="onSwitchSpeaker"
						:title="$t('rtc.speakerOn')"></div>
					<div v-show="!isSpeaker" class="icon iconfont icon-speaker-off icon-back" @click="onSwitchSpeaker"
						:title="$t('rtc.speakerOff')"></div>
					<div v-show="isVideo && isCamera" class="icon iconfont icon-camera-on icon-front"
						@click="onSwitchCamera" :title="$t('rtc.cameraOn')"></div>
					<div v-show="isVideo && !isCamera" class="icon iconfont icon-camera-off icon-back"
						@click="onSwitchCamera" :title="$t('rtc.cameraOff')"></div>
					<div v-show="isVideo && !isShareScreen" class="icon iconfont icon-share-screen icon-front"
						@click="onSwitchShareScreen" :title="$t('rtc.shareScreen')"></div>
					<div v-show="isVideo && isShareScreen" class="icon iconfont icon-share-screen-cancel icon-back"
						@click="onSwitchShareScreen" :title="$t('rtc.cancelShare')"></div>
					<el-popover placement="top" trigger="click" v-model="showDeviceMenu" width="220">
						<div class="rtc-device-menu">
							<div v-if="isVideo" class="rtc-device-menu-item">
								<div class="rtc-device-menu-label">{{ $t('rtc.cameraOn') }}</div>
								<select class="rtc-device-select" v-model="selectedCameraId"
									@change="switchCamera(selectedCameraId)">
									<option v-for="cam in cameraList" :key="cam.deviceId" :value="cam.deviceId">
										{{ cam.label || cam.deviceId }}</option>
								</select>
							</div>
							<div class="rtc-device-menu-item">
								<div class="rtc-device-menu-label">{{ $t('rtc.micOn') }}</div>
								<select class="rtc-device-select" v-model="selectedMicId"
									@change="switchMic(selectedMicId)">
									<option v-for="mic in micList" :key="mic.deviceId" :value="mic.deviceId">
										{{ mic.label || mic.deviceId }}</option>
								</select>
							</div>
						</div>
						<div slot="reference" class="icon icon-front el-icon-setting" @click="loadDeviceList"
							:title="$t('rtc.selectDevice')"></div>
					</el-popover>
					<div :title="$t('rtc.hangup')" class="icon iconfont icon-quit" @click="onQuit()"></div>
				</div>
			</div>
		</non-modal-dialog>
		<rtc-private-acceptor v-if="!isHost && isWaiting && !acceptClicked" ref="acceptor" :friend="friend" :mode="mode"
			@accept="onAccept" @reject="onReject"></rtc-private-acceptor>
	</div>
</template>

<script>
import HeadImage from '../common/HeadImage.vue';
import RtcPrivateAcceptor from './RtcPrivateAcceptor.vue';
import NonModalDialog from '../common/NonModalDialog.vue';
import AgoraRTC from 'agora-rtc-sdk-ng';
import RtcPrivateApi from '@/api/rtcPrivateApi'

export default {
	name: 'rtcPrivateVideo',
	components: {
		HeadImage,
		RtcPrivateAcceptor,
		NonModalDialog
	},
	data() {
		return {
			API: new RtcPrivateApi(), // API
			audio: new Audio(), // 呼叫音频
			showRoom: false,
			friend: {},
			isHost: false, // 是否发起人
			state: "CLOSE", // CLOSE:关闭  WAITING:等待呼叫或接听 CHATING:聊天中  ERROR:出现异常
			mode: 'video', // 模式 video:视频聊 voice:语音聊天
			userInfo: {},	// 当前用户信息
			isCamera: false, // 本地摄像头是否开启（仅视频模式）
			isMicroPhone: true, // 是否开启麦克风
			isSpeaker: true, // 是否开启扬声器
			isShareScreen: false, // 是否开启投屏
			isRemoteCamera: true,	// 对方是否开启摄像头
			isRemoteShareScreen: false, // 对方是否开启投屏
			isRemoteMicroPhone: true, // 对方是否开启麦克风
			videoTime: 0,
			videoTimer: null,
			heartbeatTimer: null,
			isFullScreen: false,
			// 声网相关
			agoraClient: null,
			localAudioTrack: null,
			localVideoTrack: null,
			remoteAudioTrack: null,
			remoteVideoTrack: null,
			channelName: '',
			agoraToken: '',
			agoraUid: null,
			isReconnecting: false, // 是否正在重连
			isPoorNetwork: false, // 网络质量是否不佳（未断线，但体验下降）
			poorNetworkStreak: 0, // 网络质量连续变差的采样次数，用于防抖
			goodNetworkStreak: 0, // 网络质量连续恢复的采样次数，用于防抖
			deviceError: null, // 设备权限/占用错误 {code, message}，非空时展示重试提示条
			pendingAction: null, // 'call' | 'accept' | null，用于重试后正确恢复呼叫/接听流程，避免重复发起信令
			acceptClicked: false, // 被叫是否已点击接听（用于点击后立刻隐藏来电弹窗，即使设备权限还没申请成功）
			cameraList: [], // 摄像头设备列表
			micList: [], // 麦克风设备列表
			selectedCameraId: '',
			selectedMicId: '',
			showDeviceMenu: false
		}
	},
	methods: {
		onFullScreenChange(isFull) {
			this.isFullScreen = isFull;
		},
		open(rtcInfo) {
			this.showRoom = true;
			this.userStore.setInRtc(true);
			this.mode = rtcInfo.mode;
			this.isHost = rtcInfo.isHost;
			this.friend = rtcInfo.friend;
			this.userInfo = this.userStore.userInfo;
			this.isCamera = this.isVideo;
			this.isRemoteCamera = this.isVideo;
			this.isRemoteMicroPhone = true;
			this.isShareScreen = false;
			this.isRemoteShareScreen = false;
			this.isMicroPhone = true;
			this.isSpeaker = true;
			this.deviceError = null;
			this.pendingAction = null;
			this.acceptClicked = false;
			if (this.isHost) {
				this.onCall();
			}
		},
		initAudio() {
			let url = require(`@/assets/audio/call.wav`);
			this.audio.src = url;
			this.audio.loop = true;
		},
		avatarBlurBg(url, fallback) {
			if (!url) return { background: fallback };
			return { backgroundImage: 'url(' + JSON.stringify(String(url)) + ')' };
		},
		onCall() {
			if (!this.checkDevEnable()) {
				this.close();
				return;
			}
			this.startHeartBeat();
			this.pendingAction = 'call';
			this.attemptOpenLocalTracks();
		},
		onAccept() {
			if (!this.checkDevEnable()) {
				this.API.failed(this.friend.id, 'devNotSupport')
				this.close();
				return;
			}
			// 进入房间
			this.showRoom = true;
			this.userStore.setInRtc(true);
			// 停止呼叫铃声
			this.audio.pause();
			this.acceptClicked = true;
			this.pendingAction = 'accept';
			this.attemptOpenLocalTracks();
		},
		// 打开本地设备并在成功后推进呼叫/接听流程；权限被拒绝等场景下不会关闭通话窗口，而是展示重试提示条
		attemptOpenLocalTracks() {
			this.deviceError = null;
			this.openLocalTracks().then(() => {
				this.proceedAfterLocalTracksReady();
			}).catch((e) => {
				this.deviceError = this.mapDeviceError(e);
			})
		},
		// 重试按钮：仅重新打开本地设备，不会重复触发 API.setup/API.accept（由 pendingAction 状态位保证）
		retryOpenLocalTracks() {
			this.attemptOpenLocalTracks();
		},
		// pendingAction 记录"本地设备就绪后应该做什么"，成功后立刻清空，保证呼叫/接听信令只发一次
		proceedAfterLocalTracksReady() {
			if (this.pendingAction === 'call') {
				this.pendingAction = null;
				// 发起呼叫
				this.API.setup(this.friend.id, this.mode).then(() => {
					// 进入等待状态
					this.state = "WAITING";
					// 播放呼叫铃声
					this.audio.play();
				}).catch(() => {
					this.close();
				})
			} else if (this.pendingAction === 'accept') {
				this.pendingAction = null;
				this.API.accept(this.friend.id);
				// 被叫在 RTC_SETUP 阶段已经拿到了自己的频道信息/token，直接加入
				this.joinAgoraChannel(this.channelName, this.agoraToken);
			}
		},
		// 把声网 SDK 抛出的错误归类为清晰的提示文案
		mapDeviceError(e) {
			const code = e && e.code;
			if (code === 'PERMISSION_DENIED') {
				return { code, message: this.$t('rtc.permissionDenied') };
			}
			if (code === 'DEVICE_NOT_FOUND') {
				return { code, message: this.$t('rtc.deviceNotFound') };
			}
			if (code === 'NOT_READABLE') {
				return { code, message: this.$t('rtc.deviceBusy') };
			}
			return { code: code || 'UNKNOWN', message: this.isVideo ? this.$t('rtc.openCameraFailed') : this.$t('rtc.openMicFailed') };
		},
		onReject() {
			// 退出通话
			this.API.reject(this.friend.id);
			// 退出
			this.close();
		},
		onHandup() {
			this.API.handup(this.friend.id)
			this.$message.success(this.$t('rtc.hungUpCallEnd'))
			this.close();
		},
		onCancel() {
			this.API.cancel(this.friend.id)
			this.$message.success(this.$t('rtc.cancelCallEnd'))
			this.close();
		},
		onSwitchMicroPhone() {
			this.isMicroPhone = !this.isMicroPhone;
			if (this.localAudioTrack) {
				this.localAudioTrack.setEnabled(this.isMicroPhone);
			}
			this.sendDeviceInfo();
		},
		onSwitchSpeaker() {
			this.isSpeaker = !this.isSpeaker;
			if (this.remoteAudioTrack) {
				this.remoteAudioTrack.setVolume(this.isSpeaker ? 100 : 0);
			}
		},
		onSwitchCamera() {
			this.isCamera = !this.isCamera
			if (this.localVideoTrack) {
				this.localVideoTrack.setEnabled(this.isCamera);
			}
			this.sendDeviceInfo();
		},
		onSwitchShareScreen() {
			if (this.isShareScreen) {
				this.endShareScreen();
			} else {
				this.startShareScreen();
			}
		},
		startShareScreen() {
			AgoraRTC.createScreenVideoTrack({}, 'disable').then((screenTrack) => {
				const unpublish = this.localVideoTrack ? this.agoraClient.unpublish(this.localVideoTrack) : Promise.resolve();
				return unpublish.then(() => {
					if (this.localVideoTrack) {
						this.localVideoTrack.close();
					}
					this.localVideoTrack = screenTrack;
					return this.agoraClient.publish(this.localVideoTrack);
				}).then(() => {
					this.localVideoTrack.play(this.$refs.localVideoContainer);
					this.isShareScreen = true;
					this.sendDeviceInfo();
				})
			}).catch(() => { });
		},
		endShareScreen() {
			this.isShareScreen = false;
			const unpublish = this.localVideoTrack ? this.agoraClient.unpublish(this.localVideoTrack) : Promise.resolve();
			unpublish.then(() => {
				if (this.localVideoTrack) {
					this.localVideoTrack.close();
					this.localVideoTrack = null;
				}
				if (this.isCamera) {
					return AgoraRTC.createCameraVideoTrack({ cameraId: this.selectedCameraId || undefined }).then((track) => {
						this.localVideoTrack = track;
						return this.agoraClient.publish(this.localVideoTrack);
					});
				}
			}).then(() => {
				if (this.localVideoTrack) {
					this.localVideoTrack.play(this.$refs.localVideoContainer);
				}
				this.sendDeviceInfo();
			}).catch(() => { });
		},
		sendDeviceInfo() {
			this.API.device({
				uid: this.friend.id,
				isCamera: this.isCamera,
				isShareScreen: this.isShareScreen,
				isMicroPhone: this.isMicroPhone
			});
		},
		onRTCMessage(msg) {
			// 除了发起通话，如果在关闭状态就无需处理
			if (msg.type != this.$enums.MESSAGE_TYPE.RTC_SETUP_VOICE &&
				msg.type != this.$enums.MESSAGE_TYPE.RTC_SETUP_VIDEO &&
				this.isClose) {
				return;
			}
			// RTC信令处理
			switch (msg.type) {
				case this.$enums.MESSAGE_TYPE.RTC_SETUP_VOICE:
					this.onRTCSetup(msg, 'voice')
					break;
				case this.$enums.MESSAGE_TYPE.RTC_SETUP_VIDEO:
					this.onRTCSetup(msg, 'video')
					break;
				case this.$enums.MESSAGE_TYPE.RTC_ACCEPT:
					this.onRTCAccept(msg)
					break;
				case this.$enums.MESSAGE_TYPE.RTC_REJECT:
					this.onRTCReject(msg)
					break;
				case this.$enums.MESSAGE_TYPE.RTC_CANCEL:
					this.onRTCCancel(msg)
					break;
				case this.$enums.MESSAGE_TYPE.RTC_FAILED:
					this.onRTCFailed(msg)
					break;
				case this.$enums.MESSAGE_TYPE.RTC_HANDUP:
					this.onRTCHandup(msg)
					break;
				case this.$enums.MESSAGE_TYPE.RTC_PRIVATE_DEVICE:
					this.onRTCPrivateDevice(msg)
					break;
			}
		},
		onRTCSetup(msg, mode) {
			this.isHost = false;
			this.mode = mode;
			this.isRemoteCamera = this.isVideo;
			this.isRemoteShareScreen = false;
			this.isRemoteMicroPhone = true;
			if (msg.content) {
				const content = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
				this.channelName = content.channelName || '';
				this.agoraToken = content.agoraToken || '';
			}
			// 对方既然能发起呼叫必然已是好友，直接查本地缓存，避免来电这一刻依赖一次额外的网络请求
			let friend = this.friendStore.findFriend(msg.sendId);
			if (!friend) {
				friend = {
					id: msg.sendId,
					showNickName: this.$t('home.unknownUser'),
					headImage: ''
				}
			}
			this.friend = friend;
			this.state = "WAITING";
			this.audio.play();
			this.startHeartBeat();
		},
		onRTCAccept(msg) {
			if (msg.selfSend) {
				this.$message.success(this.$t('rtc.acceptedOnOtherDevice'));
				this.close();
				return;
			}
			// 对方接受了的通话，content 携带的是主叫方自己的频道信息/token
			let channelName = this.channelName;
			let hostToken = this.agoraToken;
			if (msg.content) {
				const content = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
				if (content.channelName) channelName = content.channelName;
				if (content.agoraToken) hostToken = content.agoraToken;
			}
			this.joinAgoraChannel(channelName, hostToken);
		},
		onRTCReject(msg) {
			if (msg.selfSend) {
				this.$message.success(this.$t('rtc.rejectedOnOtherDevice'));
				this.close();
			} else {
				this.$message.error(this.$t('rtc.rejectedByPeer'));
				this.close();
			}
		},
		onRTCFailed(msg) {
			if (msg.selfSend) {
				this.$message.error(this.$t('rtc.notAnswered'))
				this.close();
			} else {
				this.$message.error(this.$t('rtc.private.' + msg.content))
				this.close();
			}

		},
		onRTCCancel() {
			this.$message.success(this.$t('rtc.callCancelledByPeer'));
			this.close();
		},
		onRTCHandup() {
			this.$message.success(this.$t('rtc.peerHangup'));
			this.close();
		},
		onRTCPrivateDevice(msg) {
			const dev = JSON.parse(msg.content);
			this.isRemoteCamera = dev.isCamera;
			this.isRemoteShareScreen = dev.isShareScreen;
			this.isRemoteMicroPhone = dev.isMicroPhone;
		},
		// 加入声网频道并发布本地已经打开好的音视频轨道
		async joinAgoraChannel(channelName, token) {
			this.channelName = channelName;
			this.agoraClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
			this.agoraClient.on('user-published', this.onRemoteUserPublished);
			this.agoraClient.on('user-unpublished', this.onRemoteUserUnpublished);
			this.agoraClient.on('connection-state-change', this.onConnectionStateChange);
			this.agoraClient.on('network-quality', this.onNetworkQuality);
			try {
				this.agoraUid = await this.agoraClient.join(this.appId, channelName, token || null, 0);
				const tracks = [];
				if (this.localAudioTrack) tracks.push(this.localAudioTrack);
				if (this.localVideoTrack) tracks.push(this.localVideoTrack);
				if (tracks.length) {
					await this.agoraClient.publish(tracks);
				}
				this.audio.pause();
				this.state = 'CHATING';
				this.startChatTime();
				// 接通瞬间主动同步一次当前设备状态，避免对方要等我方下一次切换才知道初始静音/摄像头状态
				this.sendDeviceInfo();
			} catch (e) {
				console.log('加入声网频道失败:', e);
				this.$message.error(this.$t('rtc.rtcInitFailed'));
				this.API.failed(this.friend.id, 'devNotSupport');
				this.close();
			}
		},
		async onRemoteUserPublished(user, mediaType) {
			await this.agoraClient.subscribe(user, mediaType);
			if (mediaType === 'video') {
				this.remoteVideoTrack = user.videoTrack;
				this.$nextTick(() => {
					if (this.remoteVideoTrack) {
						this.remoteVideoTrack.play(this.$refs.remoteVideoContainer);
					}
				});
			} else if (mediaType === 'audio') {
				this.remoteAudioTrack = user.audioTrack;
				this.remoteAudioTrack.setVolume(this.isSpeaker ? 100 : 0);
				this.remoteAudioTrack.play();
			}
		},
		onRemoteUserUnpublished(user, mediaType) {
			if (mediaType === 'video') {
				this.remoteVideoTrack = null;
			} else if (mediaType === 'audio') {
				this.remoteAudioTrack = null;
			}
		},
		onConnectionStateChange(curState) {
			if (curState === 'RECONNECTING') {
				this.isReconnecting = true;
			} else if (curState === 'CONNECTED') {
				this.isReconnecting = false;
			} else if (curState === 'DISCONNECTED' && this.isChating) {
				// 声网SDK网络抖动时会先进入RECONNECTING自动重试，走到DISCONNECTED说明重试放弃了
				this.$message.error(this.$t('rtc.connectionLost'));
				this.API.handup(this.friend.id);
				this.close();
			}
		},
		// 网络质量约每2秒上报一次（1-6，数值越大越差）；用连续2次判定做简单防抖，避免质量在临界值抖动时提示条闪烁
		onNetworkQuality(stats) {
			const worst = Math.max(stats.uplinkNetworkQuality || 0, stats.downlinkNetworkQuality || 0);
			if (worst >= 4) {
				this.poorNetworkStreak++;
				this.goodNetworkStreak = 0;
				if (this.poorNetworkStreak >= 2) {
					this.isPoorNetwork = true;
				}
			} else {
				this.goodNetworkStreak++;
				this.poorNetworkStreak = 0;
				if (this.goodNetworkStreak >= 2) {
					this.isPoorNetwork = false;
				}
			}
		},
		loadDeviceList() {
			AgoraRTC.getCameras().then((list) => { this.cameraList = list; }).catch(() => { });
			AgoraRTC.getMicrophones().then((list) => { this.micList = list; }).catch(() => { });
		},
		switchCamera(deviceId) {
			this.selectedCameraId = deviceId;
			if (this.localVideoTrack && this.localVideoTrack.setDevice) {
				this.localVideoTrack.setDevice(deviceId);
			}
		},
		switchMic(deviceId) {
			this.selectedMicId = deviceId;
			if (this.localAudioTrack && this.localAudioTrack.setDevice) {
				this.localAudioTrack.setDevice(deviceId);
			}
		},
		// 打开本地摄像头/麦克风（呼叫等待应答阶段 / 接听前均调用，用于本地预览，加入频道时再一并发布）
		openLocalTracks() {
			const tasks = [AgoraRTC.createMicrophoneAudioTrack({ microphoneId: this.selectedMicId || undefined })];
			if (this.isVideo) {
				tasks.push(AgoraRTC.createCameraVideoTrack({ cameraId: this.selectedCameraId || undefined }));
			}
			return Promise.allSettled(tasks).then((results) => {
				const audioResult = results[0];
				const videoResult = results[1];
				// 麦克风是通话的硬性前提；如果摄像头恰好创建成功但麦克风失败，需要把摄像头关掉，避免遗留一个没人用的摄像头轨道
				if (audioResult.status === 'rejected') {
					if (videoResult && videoResult.status === 'fulfilled') {
						videoResult.value.close();
					}
					console.log("打开麦克风失败:" + audioResult.reason.message)
					throw audioResult.reason;
				}
				if (this.isVideo && videoResult.status === 'rejected') {
					audioResult.value.close();
					console.log("打开摄像头失败:" + videoResult.reason.message)
					throw videoResult.reason;
				}
				this.localAudioTrack = audioResult.value;
				this.localAudioTrack.setEnabled(this.isMicroPhone);
				if (this.isVideo) {
					this.localVideoTrack = videoResult.value;
					this.isCamera = true;
					this.$nextTick(() => {
						this.localVideoTrack.play(this.$refs.localVideoContainer);
					});
				} else {
					this.isCamera = false;
				}
			})
		},
		startChatTime() {
			this.videoTime = 0;
			this.videoTimer && clearInterval(this.videoTimer);
			this.videoTimer = setInterval(() => {
				this.videoTime++;
			}, 1000)
		},
		checkDevEnable() {
			if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
				this.$message.error(this.$t('rtc.cameraAccessFailed'));
				return false;
			}
			if (typeof AgoraRTC.checkSystemRequirements === 'function' && !AgoraRTC.checkSystemRequirements()) {
				this.$message.error(this.$t('rtc.rtcInitFailed'));
				return false;
			}
			return true;
		},
		startHeartBeat() {
			// 每15s推送一次心跳
			this.heartbeatTimer && clearInterval(this.heartbeatTimer);
			this.heartbeatTimer = setInterval(() => {
				this.API.heartbeat(this.friend.id);
			}, 15000)
		},
		close() {
			this.isFullScreen = false;
			this.showRoom = false;
			this.userStore.setInRtc(false)
			if (this.localAudioTrack) {
				this.localAudioTrack.close();
				this.localAudioTrack = null;
			}
			if (this.localVideoTrack) {
				this.localVideoTrack.close();
				this.localVideoTrack = null;
			}
			if (this.agoraClient) {
				this.agoraClient.leave().catch(() => { });
				this.agoraClient = null;
			}
			this.remoteAudioTrack = null;
			this.remoteVideoTrack = null;
			this.isReconnecting = false;
			this.isPoorNetwork = false;
			this.poorNetworkStreak = 0;
			this.goodNetworkStreak = 0;
			this.deviceError = null;
			this.pendingAction = null;
			this.acceptClicked = false;
			this.isRemoteMicroPhone = true;
			this.channelName = '';
			this.agoraToken = '';
			this.audio.pause();
			this.videoTime = 0;
			this.videoTimer && clearInterval(this.videoTimer);
			this.heartbeatTimer && clearInterval(this.heartbeatTimer);
			this.videoTimer = null;
			this.heartbeatTimer = null;
			this.state = 'CLOSE';
		},
		onQuit() {
			if (this.isChating) {
				this.onHandup()
			} else if (this.isWaiting) {
				this.onCancel();
			} else {
				this.close();
			}
		}
	},
	computed: {
		title() {
			let strTitle = `${this.modeText}-${this.friend.showNickName}`;
			if (this.isChating) {
				strTitle += `(${this.currentTime})`;
			} else if (this.isWaiting) {
				strTitle += `(${this.$t('rtc.calling')})`;
			}
			return strTitle;
		},
		currentTime() {
			let min = Math.floor(this.videoTime / 60);
			let sec = this.videoTime % 60;
			let strTime = min < 10 ? "0" : "";
			strTime += min;
			strTime += ":"
			strTime += sec < 10 ? "0" : "";
			strTime += sec;
			return strTime;
		},
		appId() {
			return this.configStore.agoraAppId || '';
		},
		isVideo() {
			return this.mode == "video"
		},
		modeText() {
			return this.isVideo ? this.$t('rtc.videoCall') : this.$t('rtc.voiceCall');
		},
		isChating() {
			return this.state == "CHATING";
		},
		isWaiting() {
			return this.state == "WAITING";
		},
		isClose() {
			return this.state == "CLOSE";
		},
		showRemoteVideo() {
			return this.isRemoteShareScreen || this.isRemoteCamera;
		},
		taskbarStatusText() {
			if (this.isChating) {
				return this.$t('rtc.inCall');
			} else if (this.isWaiting) {
				return this.$t('rtc.calling');
			} else {
				return '';
			}
		},
		windowSize() {
			return {
				width: this.isVideo ? 660 : 460,
				height: this.isVideo ? 700 : 500
			}
		}

	},
	mounted() {
		// 初始化音频文件
		this.initAudio();
		// 预加载设备列表，供设备选择菜单展示
		this.loadDeviceList();
	},
	created() {
		// 监听页面刷新事件
		window.addEventListener('beforeunload', () => {
			this.onQuit();
		});
	},
	beforeUnmount() {
		this.onQuit();
	}
}
</script>

<style lang="scss">
.rtc-private-video {
	position: relative;
	height: 100%;
	box-sizing: border-box;

	&.is-fullscreen {
		display: flex;
		flex-direction: column;
		min-height: 0;

		.rtc-video-box {
			flex: 1;
			min-height: 0;
			display: flex;
			flex-direction: column;

			.rtc-video-friend {
				flex: 1;
				min-height: 0;
				height: auto;

				video {
					object-fit: contain;
				}
			}
		}

		.rtc-voice-box {
			flex: 1;
			min-height: 0;
			height: auto;
		}

		.rtc-control-bar {
			flex-shrink: 0;
		}
	}

	.el-loading-text {
		color: white !important;
		font-size: 16px !important;
	}

	.path {
		stroke: white !important;
	}

	.rtc-video-el {
		width: 100%;
		height: 100%;
	}

	.rtc-reconnect-banner {
		position: absolute;
		top: 12px;
		left: 50%;
		transform: translateX(-50%);
		z-index: 100000;
		padding: 6px 16px;
		border-radius: 4px;
		background: rgba(0, 0, 0, 0.6);
		color: white;
		font-size: 13px;
	}

	.rtc-device-error-banner {
		display: flex;
		align-items: center;
		gap: 12px;
		max-width: 80%;

		.rtc-retry-btn {
			flex-shrink: 0;
			cursor: pointer;
			text-decoration: underline;
			color: var(--im-color-primary-light-3, #79bbff);

			&:hover {
				color: white;
			}
		}
	}

	.rtc-remote-mic-badge {
		position: absolute;
		left: 12px;
		bottom: 12px;
		z-index: 100000;
		width: 28px;
		height: 28px;
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.55);
		color: white;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;

		&.rtc-remote-mic-badge-voice {
			position: absolute;
			left: 50%;
			bottom: -8px;
			transform: translateX(-50%);
		}
	}

	.rtc-device-menu {
		.rtc-device-menu-item {
			margin-bottom: 10px;

			&:last-child {
				margin-bottom: 0;
			}
		}

		.rtc-device-menu-label {
			font-size: 12px;
			color: var(--im-text-color-secondary, #909399);
			margin-bottom: 4px;
		}

		.rtc-device-select {
			width: 100%;
			height: 30px;
			border-radius: 4px;
			border: 1px solid #dcdfe6;
			padding: 0 8px;
		}
	}

	.rtc-video-box {
		position: relative;
		background: #333;

		.rtc-video-friend {
			position: relative;
			height: 590px;
			z-index: 99999;

			.rtc-avatar-panel {
				position: absolute;
				inset: 0;
				display: flex;
				align-items: center;
				justify-content: center;
				overflow: hidden;
			}

			.rtc-avatar-bg {
				position: absolute;
				inset: 0;
				background-size: cover;
				background-position: center;
				filter: blur(24px);
				transform: scale(1.1);
			}

			.rtc-avatar-dim {
				position: absolute;
				inset: 0;
				background: rgba(0, 0, 0, 0.4);
			}

			.rtc-avatar-front {
				position: relative;
				z-index: 1;
			}

			video {
				width: 100%;
				height: 100%;
				object-fit: cover;
				background: #000;

				&.rtc-video-remote-share {
					object-fit: contain;
				}
			}
		}

		.rtc-video-mine {
			position: absolute;
			z-index: 99999;
			right: 1px;
			bottom: 1px;
			width: 200px;
			height: 150px;
			border-radius: 4px;
			overflow: hidden;
			border: var(--im-border);
			box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);

			video {
				position: absolute;
				inset: 0;
				width: 100%;
				height: 100%;
				object-fit: cover;
				transform: rotateY(180deg);

				&.rtc-video-local-share {
					transform: none;
					object-fit: contain;
					background: #000;
				}
			}

			.rtc-avatar-panel-mine {
				position: absolute;
				inset: 0;
				display: flex;
				align-items: center;
				justify-content: center;
				overflow: hidden;
			}

			.rtc-avatar-panel-mine .rtc-avatar-bg,
			.rtc-avatar-panel-mine .rtc-avatar-dim {
				position: absolute;
				inset: 0;
			}

			.rtc-avatar-panel-mine .rtc-avatar-bg {
				background-size: cover;
				background-position: center;
				filter: blur(24px);
				transform: scale(1.1);
			}

			.rtc-avatar-panel-mine .rtc-avatar-dim-mine {
				background: rgba(0, 0, 0, 0.35);
			}

			.rtc-avatar-panel-mine .rtc-avatar-front {
				flex-shrink: 0;
			}
		}
	}

	.rtc-voice-box {
		position: relative;
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 390px;
		background-color: var(--im-color-primary-light-9);

		.rtc-voice-name {
			text-align: center;
			font-size: 20px;
			font-weight: 600;
		}
	}

	.rtc-control-bar {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 68px;
		background-color: var(--im-background-active);
		border-top: 1px solid #d9d9d9;
		gap: 22px;

		.icon {
			border-radius: 50%;
			padding: 10px;
			font-size: 22px;
			cursor: pointer;
			transition: all 0.3s ease;

			&:hover {
				transform: scale(1.1) translateY(-2px);
			}
		}

		.icon-front {
			color: #4E5461;
			background-color: white;
		}

		.icon-back {
			color: white;
			background-color: #4E5461;
		}

		.icon-quit {
			color: white;
			background-color: #E14949;
		}
	}
}
</style>
